import React, { useEffect, useState, useRef } from "react";
import Modal from 'react-modal';
import APIUtils from '../../../utils/APIUtils';
import {
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  TextField,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import muiInputStyles from "../../../uiComponents/MuiInput/MuiInputStyles";
import muiCheckboxStyles from '../../../uiComponents/MuiInput/MuiCheckboxStyles';

const useStyles = makeStyles({ // custom styles for the modal
  readOnlyInput: {
    cursor: 'not-allowed',
  },
  customErrorText: {
    '&.Mui-error': {
      color: '#DA190B', 
    },
  },
});

const literatureModal = (props) => {
  const [pmid, setPmid] = useState(props.literatureReview.pmid);
  const [comment, setComment] = useState(props.literatureReview.comment);
  const [acmgCriteriaId, setAcmgCriteriaId] = useState(props.literatureReview.acmgEvidCriteriaId?.split(",") || []);
  const [literatureCategory, setLiteratureCategory] = useState(props.literatureCategory);
  const [errors, setErrors] = useState({ pmid: '', category: '', criteria: '', comment: '' });
  const [checkboxFocus, setCheckboxFocus] = useState(false); // Track focus state for checkboxes
  const [checkboxValidationError, setCheckboxValidationError] = useState(false); // Track validation error for checkboxes
  const [isSaving, setIsSaving] = useState(false); // Track saving state to give the user immediate feedback
  const selectCategory = useRef(null);
  const selectCriteria = useRef(null);
  const inputPmid = useRef(null);
  const inputComment = useRef(null);
  const programmaticFocus = useRef(false); // Track whether focus is programmatic
  const classes = muiInputStyles();
  const customClasses = useStyles();
  const isReadOnly = props.literatureReview.pmid.length > 0;
  const checkboxClasses = muiCheckboxStyles();

  useEffect(() => {
    // Check if the modal is open and if we are in add or edit state
    if (props.modalIsOpen) {
      if (props.literatureReview.pmid.length === 0) {
        // Add state: Clear the input fields
        setPmid('');
        setComment('');
        setAcmgCriteriaId([]);
        setLiteratureCategory('');
      } else {
        // Edit state: Set the inputs with existing literature review data
        setPmid(props.literatureReview.pmid);
        setComment(props.literatureReview.comment);
        setAcmgCriteriaId(props.literatureReview.acmgEvidCriteriaId?.split(",") || []);
        setLiteratureCategory(props.literatureReview.literatureCategory);
      }
    } else {
      // Clear errors and saving state when the modal is closed
      setErrors({ pmid: '', category: '', criteria: '', comment: '' });
      setCheckboxFocus(false); // Reset checkbox focus state
      setIsSaving(false); // Reset saving state so the button is ready on next open
    }
  }, [props.modalIsOpen, props.literatureReview]);
  
  useEffect(() => {
    document.body.setAttribute('aria-hidden', 'false');
  },[]); 

  const validateFields = () => {
    const newErrors = { pmid: '', category: '', criteria: '', comment: '' };
    let firstInvalidField = null;
  
    if (!pmid) {
      newErrors.pmid = 'PMID is required.';
      if (!firstInvalidField) firstInvalidField = inputPmid;
    }
  
    if (!literatureCategory) {
      newErrors.category = 'Please select a category.';
      if (!firstInvalidField) firstInvalidField = selectCategory;
    }
  
    if (!acmgCriteriaId.length) {
      newErrors.criteria = 'Please select at least one criterion.';
      setCheckboxValidationError(true); // Set validation error for checkboxes
      setCheckboxFocus(true); // Set focus indicator for the first checkbox
      programmaticFocus.current = true; // Indicate that the next focus event is programmatic
      if (!firstInvalidField) firstInvalidField = selectCriteria;
    } else {
      setCheckboxValidationError(false); // Clear validation error for checkboxes
    }
  
    if (!comment) {
      newErrors.comment = 'Comment is required.';
      if (!firstInvalidField) firstInvalidField = inputComment;
    }
  
    setErrors(newErrors);
  
    // Focus on the first invalid field
    if (firstInvalidField && firstInvalidField.current) {
      firstInvalidField.current.focus();
    }
  
    return !Object.values(newErrors).some((error) => error !== '');
  };

  const handleCheckboxInteraction = () => {
    setCheckboxFocus(false); // Clear focus indicator when a checkbox is clicked or interacted with
    // Clear validation error if a valid selection is made
    if (acmgCriteriaId.length > 0) {
      setErrors((prevErrors) => ({ ...prevErrors, criteria: '' })); // Clear the criteria error
      setCheckboxValidationError(false); // Clear validation error state
    }
  };

  async function saveLiteratureReview() {
    if (!validateFields()) {
      return;
    }

    let formData = new FormData();
    formData.append('pmid', pmid);
    formData.append('comment', comment);
    formData.append('acmgCriteriaId', acmgCriteriaId.join(","));
    formData.append('literatureCategory', literatureCategory);

    setIsSaving(true);
    try {
      await APIUtils.saveLiteratureReview(props.variantInterpretationId, formData);
      // Keep the spinner visible while the parent refreshes data and closes the
      // modal. isSaving is reset when the modal closes (see effect above), which
      // avoids the button flashing back to its default state before it hides.
      await props.onAddOrUpdate('includedLiteratureTable');
    } catch (error) {
      setIsSaving(false);
    }
  }

  const handleOnChange = (e) => {
    const newValue = e.target.value;
    setLiteratureCategory(newValue);
    if (newValue) {
      setErrors((prevErrors) => ({ ...prevErrors, category: '' }));
    }
    setAcmgCriteriaId([]);
  };

  const onEvaluationCheckboxChange = (value) => {
    setAcmgCriteriaId((prev) =>
      prev?.includes(value) ? prev.filter((item) => item !== value) : [...(prev || []), value]
    );
  
    // Clear validation error if a valid selection is made
    if (acmgCriteriaId.length > 0 || !acmgCriteriaId.includes(value)) {
      setErrors((prevErrors) => ({ ...prevErrors, criteria: '' }));
      setCheckboxValidationError(false);
    }
  };

  const renderCheckboxItems = () => {
    let isFirstCheckbox = true; // Track the first checkbox
  
    return props.categories
      .filter((category) => category.literatureCategoryValue === literatureCategory)
      .map((filterCategory) =>
        filterCategory.acmgCriterias.map((category) => {
          const disabled = acmgCriteriaId.length > 0 ? category.charAt(0) !== acmgCriteriaId[0].charAt(0) : false;
          const checked = acmgCriteriaId?.includes(category);
  
          // Apply the ref only to the first checkbox
          const ref = isFirstCheckbox ? selectCriteria : null;
          const focusClass =
            isFirstCheckbox && checkboxFocus && checkboxValidationError
              ? checkboxClasses.checkboxFocus
              : ''; // Add focus class dynamically only when validation error exists
          isFirstCheckbox = false; // After the first checkbox, set this to false
  
          return (
            <FormControlLabel
              key={category}
              classes={{ label: checkboxClasses.checkboxLabel }}
              control={
                <Checkbox
                  inputRef={ref} // Apply ref dynamically
                  className={`${checkboxClasses.checkboxRoot} ${focusClass}`} // Add focus class dynamically
                  name={category}
                  classes={{
                    root: checked ? checkboxClasses.checkboxSelected : checkboxClasses.checkboxRoot,
                  }}
                  disabled={disabled}
                  value={category}
                  checked={checked}
                  onChange={() => {
                    onEvaluationCheckboxChange(category);
                    handleCheckboxInteraction(); // Clear focus indicator and validation error on interaction
                  }}
                  onFocus={() => {
                    if (programmaticFocus.current) {
                      // Reset the flag after handling the programmatic focus
                      programmaticFocus.current = false;
                    } else {
                      // Clear custom focus indicator when moving to another checkbox
                      setCheckboxFocus(false);
                    }
                  }}
                />
              }
              label={category}
            />
          );
        })
      );
  };

  return (
    <Modal
      className="modal"
      overlayClassName="modalOverlay"
      aria-modal="true"
      contentLabel="Add Literature"
      shouldCloseOnOverlayClick={true}
      isOpen={props.modalIsOpen}
      onRequestClose={props.closeModal}
    >
      <div className="modalHeader">Add Literature Evidence</div>
      <div className="modalContent">
        <form>
          <div className="muiFormItem">
            <FormControl fullWidth error={!!errors.pmid}>
              <TextField
                fullWidth
                size="small"
                id="pmid"
                name="pmid"
                type="text"
                inputMode="numeric"
                className={classes.muiInputs}
                value={pmid || ''}
                inputRef={inputPmid}
                onChange={(e) => {
                  const newValue = e.target.value.replace(/\D/g, ''); // Allow only numeric input
                  if (newValue.length <= 8) {
                    setPmid(newValue);
                    // Clear validation error for PMID if a valid value is entered
                    if (newValue) {
                      setErrors((prevErrors) => ({ ...prevErrors, pmid: '' }));
                    }
                  }
                }}
                variant="outlined"
                label="PMID"
                placeholder="Enter PMID"
                inputProps={{
                  readOnly: isReadOnly,
                  "aria-readonly": isReadOnly,
                  className: isReadOnly ? customClasses.readOnlyInput : '',
                  "aria-required": true,
                  maxLength: 8,
                }}
                required
              />
              <FormHelperText className={customClasses.customErrorText}>{errors.pmid}</FormHelperText>
            </FormControl>
          </div>

          <div className="muiFormItem">
            <FormControl
              fullWidth
              error={!!errors.category}
            >
              <TextField
                fullWidth
                size="small"
                id="litCategory"
                name="category"
                className={classes.muiInputs}
                value={literatureCategory || ''}
                inputRef={selectCategory}
                onChange={handleOnChange}
                variant="outlined"
                label="Type of Evidence"
                select
                required
                role="combobox" // MUI role modification for aria-required and additional required elements
                aria-required="true"
                aria-controls="litCategory"
                aria-expanded="false"
                SelectProps={{ 
                  MenuProps: { 
                    disableScrollLock: true, 
                    disablePortal: true, // makes the popover render inside the normal React tree (instead of attaching it to the body)
                  }, 
                }}
              >
                {props.categories.map((category, index) => (
                  <MenuItem key={index} value={category.literatureCategoryValue}>
                    {category.literatureCategoryLabel}
                  </MenuItem>
                ))}
              </TextField>
              <FormHelperText className={customClasses.customErrorText}>{errors.category}</FormHelperText>
            </FormControl>
          </div>

          <div className="muiFormItem">
          {literatureCategory && (
            <FormControl 
              fullWidth 
              error={!!errors.criteria} 
            >
              <FormLabel
                component="legend"
                className={checkboxClasses.formLabel}
                style={{ color: 'var(--alt-black)' }}
                ref={selectCriteria} // Attach ref here
                required
              >
                Evaluation Criteria
              </FormLabel>
              {renderCheckboxItems()} {/* Directly render the checkboxes */}
              <FormHelperText className={customClasses.customErrorText}>{errors.criteria}</FormHelperText>
            </FormControl>
          )}
        </div>

          <div className="muiFormItem">
            <FormControl fullWidth error={!!errors.comment}>
              <TextField
                fullWidth
                size="small"
                id="comments"
                name="comments"
                className={classes.muiInputs}
                value={comment}
                inputRef={inputComment}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setComment(newValue);
                  if (newValue) {
                    setErrors((prevErrors) => ({ ...prevErrors, comment: '' }));
                  }
                }}
                variant="outlined"
                label="Comments"
                multiline
                minRows={6}
                inputProps={{
                  maxLength: 1000,
                  "aria-required": true,
                }}
                required
              />
              <FormHelperText className={customClasses.customErrorText}>{errors.comment}</FormHelperText>
            </FormControl>
          </div>
        </form>
      </div>
      <div className="modalButtons">
        <button className="modalButton" onClick={props.closeModal} disabled={isSaving}>
          Cancel
        </button>
        <button className="modalButton primaryButton" onClick={saveLiteratureReview} disabled={isSaving}>
          {isSaving ? (
            <span className="savingLabel">
              <CircularProgress aria-label="Saving" color="inherit" size="1em" />
              Saving...
            </span>
          ) : (
            'Save'
          )}
        </button>
      </div>
    </Modal>
  );
}

export default literatureModal;